/* @flow */

import { logError } from './logger';
import { noop, isValid } from './coreutils';

/**
 * @module scheduler
 * @description Implements scheduler functionality
 * @requires module:utils/coreutils
 * @requires module:utils/logger
 */

/**
 * @class _scheduler
 * @description Scheduler base class
 */
export default class _scheduler<T> {
  _queue: Array<adInfo>;

  _tid: ?T;

  delay: number;

  // used only by timedScheduler
  CB: cbFunc;

  prioritizeCB: (Array<adInfo>) => ?number;

  endCB: () => void;

  shouldProcessImmediately: adInfo => boolean;

  name2: string;

  getName(): string {
    return this.name2;
  }

  static isSupported(): boolean {
    return true;
  }

  constructor(n: string = '') {
    this.name2 = n;
    this._queue = [];
    this.delay = 200;
    // FlowFixMe
    this.CB = noop;
    this.prioritizeCB = noop;
    this.endCB = noop;
    this.shouldProcessImmediately = () => false;
  }

  /**
   * set properties
   *
   * @param {number} d - Delay
   * @param {Function} cb - callback function to be invoked for each job
   * @param {Function} pCB - callback for priortizing. Takes input list and returns index
   * @param {Function} eCB - callback function to be invoked after processing all the queued jobs
   * @param {Function} iCB - callback function to check whether to process immediately or not
   * @returns {undefined}
   */
  setProperties(
    d: number,
    cb?: cbFunc,
    pCB?: cbFunc,
    eCB?: cbFunc,
    iCB?: cbFunc
  ) {
    this.delay = d;
    // $FlowFixMe[constant-condition]
    this.CB = cb || (() => logError('scheduler job queued without callback'));
    // $FlowFixMe[constant-condition]
    this.prioritizeCB = pCB || (() => 0);
    this.endCB = eCB || noop;
    this.shouldProcessImmediately = iCB || (() => false);
  }

  /**
   * enqueues obj in queue and triggers processing
   *
   * @param {module:adloader.adInfo} obj - Object to be processed on timer callback
   * @returns {undefined}
   */
  enqueue(obj: adInfo) {
    if (this.shouldProcessImmediately(obj)) {
      // process this immediately
      this.CB(obj);
    } else {
      this._queue.push(obj);
    }
    this._processQueue();
  }

  /**
   *
   * Checks the queue for any pending tasks and act accordingly
   *
   * @returns {undefined}
   * @memberof _scheduler
   */
  checkQueue() {
    // there are pending entries to processed
    if (this._isPending()) {
      return;
    }

    // processing completed trigger end of processing CB
    this.endCB();
  }

  /**
   * Clear queue and stop the timer
   *
   * @returns {undefined}
   */
  clear() {
    this._queue.length = 0;
    this._clear();
  }

  /**
   * Process the pending tasks
   *
   * @access private
   * @returns {undefined}
   */
  _processQueue() {
    // schedule only if there is no schedule currently
    if (!this._isPending() && this._queue.length) {
      this._schedule();
    }

    this.checkQueue();
  }

  /**
   * Process specific entry from queue
   *
   * @access private
   * @returns {undefined}
   */
  _processEntry() {
    // reset the timer
    this._clear();
    if (this._queue.length) {
      // prioritize
      const ind = this.prioritizeCB(this._queue) || 0;
      const [obj] = this._queue.splice(Math.max(ind, 0), 1);
      this.CB(obj);
    }

    // process rest of the queue
    this._processQueue();
  }

  /**
   * Check if there are any pending tasks in queue
   *
   * @access private
   * @returns {boolean} - Queue status
   * @memberof _scheduler
   */
  _isPending(): boolean {
    return isValid(this._tid);
  }

  _schedule() {
    /**/
  }

  _clear() {
    /**/
  }
}
